import { PURCHASE_FIELDS, PURCHASE_CHECKS, purchaseDefaults } from './purchasePlan.js'
import { safeResearchUrl } from './classicResearch.js'
import { SCENARIO_FIELDS } from './specialistScenario.js'
import { classicReferenceFingerprint } from './classicSourceReview.js'

export const PLAN_PREFIX = 'automargin_plan_v1:'
export const PLAN_EVENT = 'automargin-plans-changed'
export const MAX_PLANS = 100
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const text = value => typeof value === 'string' && value.length <= 160 ? value : ''
// Keep unfinished/invalid inputs exactly as typed. The existing calculators,
// not storage, decide whether the assumptions are usable.
const numericText = value => typeof value === 'string' && value.length <= 32 ? value : ''
export const SPECIALIST_CHOICES = Object.freeze({
  odometer: ['unknown', 'documented', 'replaced'],
  condition: ['unknown', 'project', 'driver', 'documented'],
  exitChannel: ['nl', 'trade', 'international'],
})

// A saved exit assumption must be reconsidered when the comparison becomes
// incomplete or a source check changes/expires, even if the asking prices agree.
// Store binding metadata only, never an inferred condition premium or bid.
export const specialistDraftContext = lead => lead ? JSON.stringify([
  lead.anchor, lead.peers.map(classicReferenceFingerprint), Boolean(lead.referencesTruncated),
  [lead.sourceReview, ...(lead.referenceReviews || [])].map(review => review
    ? [review.status, review.checked_at, review.source_url] : null),
]) : 'browse:context-not-bound'

export function planIdentity(car, kind) {
  const id = String(car?.listing_id || '')
  const country = car?.country_code
  if (!['purchase', 'classic', 'specialist'].includes(kind) || !/^\d{1,18}$/.test(id) || !['DE', 'IT', 'FR', 'AT'].includes(country)) return null
  const make = text(car.make || car.brand), model = text(car.model), year = Number(car.year)
  if (!make || !model || !Number.isInteger(year) || year < 1886 || year > 2200) return null
  return { kind, listing_id: id, country_code: country, make, model, year,
    key: `${PLAN_PREFIX}${kind}:${country}:${id}` }
}

/** Save inputs, never a profit prediction, current recommendation or source verdict.
 * The fingerprint binds a restored form to the exact published observation. */
export function planFingerprint(car, kind, context = '') {
  const identity = planIdentity(car, kind)
  if (!identity || !car.decision_snapshot_id || !car.model_git_sha || !car.scraped_at) return null
  return JSON.stringify([identity.kind, identity.country_code, identity.listing_id, identity.make, identity.model, identity.year,
    car.decision_snapshot_id, car.model_git_sha, car.scraped_at, car.price_amount_eur,
    car.mileage_km ?? car.mileage, car.horsepower_hp ?? car.power, car.fuel_type ?? car.fuel,
    car.transmission_original ?? car.transmission, car.car_type ?? car.body_type, car.source_checked_at, car.source_flag,
    car.source_real_url, car.detail_page_url ?? car.detail_url,
    ...(kind === 'purchase' ? [car.pred_target_price, car.pred_target_price_lower_80, car.pred_target_price_upper_80,
      car.import_fixed_costs, car.bpm_rest, car.potential_vat_liability, car.expected_revenue] : [context])])
}

export function planPayload(kind, payload = {}) {
  if (kind === 'classic') return { allowance: numericText(payload.allowance) }
  if (kind === 'specialist') return {
    inputs: Object.fromEntries(SCENARIO_FIELDS.map(key => [key, numericText(payload.inputs?.[key])])),
    choices: Object.fromEntries(Object.entries(SPECIALIST_CHOICES).map(([key, allowed]) =>
      [key, allowed.includes(payload.choices?.[key]) ? payload.choices[key] : allowed[0]])),
  }
  const inputs = Object.fromEntries(PURCHASE_FIELDS.map(key => [key, numericText(payload.inputs?.[key])]))
  const checks = Object.fromEntries(PURCHASE_CHECKS.map(([key]) => [key, payload.checks?.[key] === true]))
  return { inputs, checks, quick: { allowance: numericText(payload.quick?.allowance), discountPct: numericText(payload.quick?.discountPct) } }
}

export const emptyPlanPayload = (car, kind) => kind === 'classic' ? { allowance: '' }
  : kind === 'specialist' ? planPayload(kind)
  : { inputs: purchaseDefaults(car), checks: {}, quick: { allowance: '', discountPct: '0' } }

function validateDraft(value, key) {
  if (!object(value) || value.schema !== 'automargin-plan.v1' || !object(value.identity)) throw new Error('invalid')
  const identity = planIdentity(value.identity, value.identity.kind)
  if (!identity || identity.key !== key || typeof value.fingerprint !== 'string' || value.fingerprint.length > 50000 ||
      !value.fingerprint || typeof value.revision !== 'string' || value.revision.length > 100 ||
      !/^\d{4}-\d\d-\d\dT.*Z$/.test(value.saved_at) || !Number.isFinite(Date.parse(value.saved_at)) || !object(value.payload)) throw new Error('invalid')
  const fields = identity.kind === 'classic' ? [value.payload.allowance]
    : identity.kind === 'specialist' ? SCENARIO_FIELDS.map(key => value.payload.inputs?.[key])
    : [...PURCHASE_FIELDS.map(key => value.payload.inputs?.[key]), value.payload.quick?.allowance, value.payload.quick?.discountPct]
  if (fields.some(v => typeof v !== 'string' || v.length > 32) ||
      (identity.kind === 'purchase' && PURCHASE_CHECKS.some(([key]) => typeof value.payload.checks?.[key] !== 'boolean')) ||
      (identity.kind === 'specialist' && Object.entries(SPECIALIST_CHOICES).some(([key, allowed]) => !allowed.includes(value.payload.choices?.[key])))) throw new Error('invalid')
  const payload = planPayload(identity.kind, value.payload)
  // Invalid or newly unknown fields must not quietly become zero or overwrite
  // an unreadable prior plan. Preserve the stored bytes and report the problem.
  return { schema: value.schema, identity, fingerprint: value.fingerprint, revision: value.revision,
    saved_at: value.saved_at, source_url: safeResearchUrl(value.source_url), payload }
}

export function browserPlanStorage() {
  try { return window.localStorage } catch { return null }
}

export function readPlanDraft(storage, key) {
  if (!storage || !key?.startsWith(PLAN_PREFIX)) return { draft: null, error: 'unavailable' }
  let raw
  try { raw = storage.getItem(key) } catch { return { draft: null, error: 'unavailable' } }
  try {
    if (raw === null) return { draft: null, error: null }
    if (raw.length > 64000) return { draft: null, error: 'invalid' }
    return { draft: validateDraft(JSON.parse(raw), key), error: null }
  } catch { return { draft: null, error: 'invalid' } }
}

export function listPlanDrafts(storage) {
  if (!storage) return { drafts: [], error: 'unavailable' }
  try {
    const drafts = [], errors = []
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key?.startsWith(PLAN_PREFIX)) continue
      const result = readPlanDraft(storage, key)
      if (result.error) errors.push(result.error)
      else if (result.draft) drafts.push(result.draft)
    }
    return { drafts: drafts.sort((a, b) => b.saved_at.localeCompare(a.saved_at)), error: errors.length ? 'invalid' : null }
  } catch { return { drafts: [], error: 'unavailable' } }
}

export function writePlanDraft(storage, { car, kind, context = '', payload, expectedRevision = null, now = new Date() }) {
  const identity = planIdentity(car, kind), fingerprint = planFingerprint(car, kind, context)
  if (!storage || !identity || !fingerprint) return { draft: null, error: 'unavailable' }
  if (fingerprint.length > 50000) return { draft: null, error: 'size' }
  const previous = readPlanDraft(storage, identity.key)
  if (previous.error) return previous
  if ((previous.draft?.revision ?? null) !== expectedRevision) return { draft: null, error: 'conflict' }
  const listing = listPlanDrafts(storage)
  if (listing.error) return { draft: null, error: listing.error }
  if (!previous.draft && listing.drafts.length >= MAX_PLANS) return { draft: null, error: 'limit' }
  try {
    const draft = { schema: 'automargin-plan.v1', identity, fingerprint,
      revision: `${now.toISOString()}:${Math.random().toString(36).slice(2)}`, saved_at: now.toISOString(),
      source_url: safeResearchUrl(car.source_real_url) || safeResearchUrl(car.external_link) ||
        safeResearchUrl(car.source_ad_url) || safeResearchUrl(car.detail_page_url) || safeResearchUrl(car.detail_url),
      payload: planPayload(kind, payload) }
    const serialized = JSON.stringify(draft)
    if (serialized.length > 64000) return { draft: null, error: 'size' }
    storage.setItem(identity.key, serialized)
    return { draft, error: null }
  } catch { return { draft: null, error: 'unavailable' } }
}

export function deletePlanDraft(storage, draft) {
  const previous = readPlanDraft(storage, draft?.identity?.key)
  if (previous.error) return previous.error
  if (!previous.draft || previous.draft.revision !== draft.revision) return 'conflict'
  try { storage.removeItem(draft.identity.key); return null } catch { return 'unavailable' }
}

export function restorePlanDraft(draft, car, kind, context = '', now = new Date()) {
  const fingerprint = planFingerprint(car, kind, context)
  const age = now.getTime() - Date.parse(draft?.saved_at)
  const matches = Boolean(fingerprint && draft?.fingerprint === fingerprint && Number.isFinite(age) && age >= 0 && age < 86400000)
  return { matches, payload: matches ? planPayload(kind, draft.payload) : emptyPlanPayload(car, kind) }
}

/** Explicitly reuse only personal reserves. New quotes/checks must be entered
 * again when observations change or the saved review is older than a day. */
export function portablePlanInputs(draft, car, kind) {
  if (kind === 'classic') return planPayload(kind, draft.payload)
  const payload = planPayload(kind, draft.payload)
  if (kind === 'specialist') return { ...payload,
    inputs: { ...payload.inputs, exitPrice: '', importCosts: '' },
    choices: { ...payload.choices, condition: 'unknown', odometer: 'unknown' },
  }
  return { ...payload, inputs: { ...payload.inputs, purchase: '', importCosts: '' }, checks: {},
    quick: { allowance: '', discountPct: '0' } }
}
